import React, { useState } from 'react';
import { MapPin, Search, Loader, ExternalLink, Calendar, Users, Star } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import AppLayout from '../../components/AppLayout';

export default function EventVenueDiscovery() {
  const [formData, setFormData] = useState({
    country: '',
    city: '',
    venue_type: '',
    include_possible: true,
  });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [organizers, setOrganizers] = useState([]);

  const handleSearch = async () => {
    if (!formData.city && !formData.country) {
      toast.error('Please enter at least a city or country');
      return;
    }
    
    setSearching(true);
    setResults([]); // Clear previous results
    
    try {
      console.log('Searching with data:', formData);
      const r = await api.post('/api/event-venue-discovery/search', formData);
      console.log('Search response:', r.data);
      
      const venues = r.data.venues || [];
      setResults(venues);
      
      if (venues.length === 0) {
        toast.info('No venues found. Try adjusting your search criteria.');
      } else {
        toast.success(`Found ${venues.length} venue${venues.length === 1 ? '' : 's'}`);
      }
    } catch (err) {
      console.error('Search error:', err);
      const errorMsg = err?.response?.data?.detail || err?.message || 'Search failed';
      toast.error(`Search failed: ${errorMsg}`);
    } finally {
      setSearching(false);
    }
  };

  const loadOrganizers = async () => {
    try {
      const r = await api.get('/api/event-venue-discovery/organizers');
      setOrganizers(r.data.organizers || []);
    } catch (err) {
      console.error('Load organizers error:', err);
    }
  };

  const handleReview = async (venue, action) => {
    try {
      await api.post('/api/event-venue-discovery/review', {
        venue_id: venue.id,
        action,
      });
      toast.success(action === 'approve' ? 'Venue approved!' : 'Venue rejected');
      setResults(prev => prev.filter(v => v.id !== venue.id));
    } catch (err) {
      console.error('Review error:', err);
      toast.error('Review failed');
    }
  };

  return (
    <AppLayout title="Event Venue Discovery" overline="LIVE SHOWS · EUROPE">
      <div className="max-w-7xl mx-auto">
        {/* Search Form */}
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Country</label>
              <input
                type="text"
                placeholder="e.g. Germany, Spain"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">City</label>
              <input
                type="text"
                placeholder="e.g. Berlin, Barcelona"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Venue Type</label>
              <select
                value={formData.venue_type}
                onChange={(e) => setFormData({ ...formData, venue_type: e.target.value })}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white">
                <option value="">All Types</option>
                <option value="sauna">Sauna</option>
                <option value="club">Club</option>
                <option value="bar">Bar</option>
                <option value="sex_club">Sex Club</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={searching}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30">
                {searching ? <Loader className="animate-spin" size={18} /> : <Search size={18} />}
                {searching ? 'Searching...' : 'Search Venues'}
              </button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="include_possible"
              checked={formData.include_possible}
              onChange={(e) => setFormData({ ...formData, include_possible: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="include_possible" className="text-sm text-zinc-400">Include possible venues (not just confirmed)</label>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <button
            onClick={loadOrganizers}
            className="bg-zinc-800 hover:bg-zinc-700 border border-white/10 text-white px-4 py-2 rounded-lg transition flex items-center gap-2">
            <Users size={18} />
            View Event Organizers
          </button>
        </div>

        {/* Organizers List */}
        {organizers.length > 0 && (
          <div className="bg-zinc-900 border border-white/10 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Event Organizers</h2>
            <div className="space-y-3">
              {organizers.map((org, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{org.name}</div>
                    {org.website && (
                      <a href={org.website} target="_blank" rel="noreferrer" className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1">
                        <ExternalLink size={12} /> {org.website}
                      </a>
                    )}
                  </div>
                  <div className="text-sm text-zinc-400">{org.events_hosted || 0} events</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">Results ({results.length})</h2>
            {results.map(venue => (
              <div key={venue.id} className="bg-zinc-900 border border-white/10 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{venue.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-zinc-400">
                      <span className="flex items-center gap-1"><MapPin size={14} /> {venue.city}, {venue.country}</span>
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs font-semibold">{venue.venue_type}</span>
                      {venue.event_confidence === 'confirmed' && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs font-semibold flex items-center gap-1">
                          <Star size={12} /> CONFIRMED
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {venue.description && (
                  <p className="text-zinc-300 text-sm mb-4">{venue.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {venue.website && (
                    <a href={venue.website} target="_blank" rel="noreferrer" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                      <ExternalLink size={14} /> Website
                    </a>
                  )}
                  {venue.contact_email && (
                    <span className="text-zinc-400 text-sm">✉ {venue.contact_email}</span>
                  )}
                </div>

                {venue.event_types?.length > 0 && (
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-zinc-400 uppercase mb-2 block">Event Types</span>
                    <div className="flex flex-wrap gap-2">
                      {venue.event_types.map((type, i) => (
                        <span key={i} className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-xs">{type}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleReview(venue, 'approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg transition">
                    Approve & Add to CRM
                  </button>
                  <button
                    onClick={() => handleReview(venue, 'reject')}
                    className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold py-2 rounded-lg transition">
                    Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!searching && results.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Calendar size={48} className="mx-auto mb-4 opacity-50" />
            <p>No results yet. Enter search criteria and click "Search Venues"</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
